/**
 * Session 8 Block 4 · Operator Health-Dashboard
 * Ampel-Kacheln pro Sub-Block. Klick → JSON-Panel öffnet.
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { RefreshCcw } from "lucide-react";


function StatusDot({ status }) {
  const color = {
    ok: "bg-emerald-500", ready: "bg-emerald-500",
    degraded: "bg-amber-500",
    unknown: "bg-amber-400",
    mock: "bg-sky-500",
    failed: "bg-rose-500",
    hidden: "bg-slate-300",
  }[status] || "bg-slate-400";
  return <span className={`inline-block h-2 w-2 rounded-full ${color}`} />;
}


function Tile({ title, status, note, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <StatusDot status={status} />
          {title}
        </CardTitle>
        {note && <CardDescription className="text-xs">
          {note}
        </CardDescription>}
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">
        {children}
      </CardContent>
    </Card>
  );
}


export default function OperatorHealth() {
  const [data, setData] = useState(null);
  const [showJson, setShowJson] = useState(false);

  const load = () => {
    api.get("/health/ready-plus").then((r) => setData(r.data))
       .catch(() => {});
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, []);

  if (!data) return (
    <div data-testid="operator-health-page">Lade…</div>
  );

  const ext = data.external_providers || {};

  return (
    <div className="space-y-6" data-testid="operator-health-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Health-Dashboard</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}>
            <RefreshCcw className="h-4 w-4 mr-2" /> Aktualisieren
          </Button>
          <Button variant="ghost" onClick={() => setShowJson(!showJson)}
                   data-testid="operator-health-toggle-json">
            Rohdaten
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Tile title="Mongo" status={data.mongo?.status}
               note={`Latenz: ${data.mongo?.latency_ms || 0}ms`}
               testId="operator-health-mongo" />
        <Tile title="Storage" status={data.storage?.production_ready
                                          ? "ok" : "unknown"}
               note={`Modus: ${data.storage?.mode}`}
               testId="operator-health-storage">
          {data.storage?.production_ready
            ? "Produktion tauglich"
            : "Preview-Modus (lokal)"}
        </Tile>
        <Tile title="Queue"
               status={data.queue?.workers_healthy ? "ok" : "degraded"}
               note={`Tiefe: ${data.queue?.depth || 0}`}
               testId="operator-health-queue" />
        <Tile title="OpenAI (LLM)"
               status={ext.openai?.status}
               note={ext.openai?.note}
               testId="operator-health-openai" />
        <Tile title="Meta Ads"
               status={ext.meta_ads?.status}
               note={ext.meta_ads?.note}
               testId="operator-health-meta" />
        <Tile title="Google Ads"
               status={ext.google_ads?.status}
               note={ext.google_ads?.note}
               testId="operator-health-google" />
        <Tile title="Stripe"
               status={ext.stripe?.status}
               note={ext.stripe?.note}
               testId="operator-health-stripe" />
        <Tile title="Abos"
               status="ok"
               note={`${data.subscriptions?.active_plans || 0} Pläne`}
               testId="operator-health-subs">
          {Object.entries(
            data.subscriptions?.tenants_by_plan || {})
            .map(([k, v]) => (
              <div key={k}>{k}: {v}</div>
            ))}
        </Tile>
        <Tile title="Audit (letzte Stunde)"
               status={data.recent_errors_last_hour > 0
                          ? "degraded" : "ok"}
               note={`Events: ${data.audit_events_last_hour}`}
               testId="operator-health-audit">
          Fehler: {data.recent_errors_last_hour}
        </Tile>
      </div>

      {showJson && (
        <Card>
          <CardContent className="p-4">
            <pre className="text-xs overflow-auto max-h-[400px]">
              {JSON.stringify(data, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
